import { Socket } from 'phoenix';

import getConfig from './getConfig';
import { getClientSessionToken } from 'shared/Redux/api/session';

const env = getConfig();
const channels = {};
const socketCheckTimeout = 5000;
let socket = null;
let sessionToken = null;
let connected = false;
let socketCheckInterval = null;

export const initializeSocket = () => {
  let socketHost;

  if (env.isDevelopment && !env.isProduction) {
    socketHost = '//localhost:4000/socket';
  } else {
    socketHost = `${env.apiHost.split('/api')[0]}/socket`;
  }

  // Remove existing protocol scheme
  if (socketHost.includes('//')) {
    socketHost = `//${socketHost.split('//')[1]}`;
  } else {
    socketHost = `//${socketHost}`;
  }

  if (socket && sessionToken !== getClientSessionToken()) {
    console.log('[rm] Session token changed, create new Socket');
    disconnectSocket();
  }

  sessionToken = getClientSessionToken();

  if (!socket && sessionToken) {
    socket = new Socket(socketHost, {
      params: { token: sessionToken }
    });

    socket.onOpen(() => {
      console.log('[rm] Socket connected');
      connected = true;
    });

    socket.onError(() => {
      console.error('[rm] Socket error');
      connected = false;

      const newSessionToken = getClientSessionToken();

      if (!!newSessionToken && newSessionToken !== sessionToken) {
        console.log('[rm] Session token changed, reconnecting');
        initializeSocket();
      }
    });

    socket.onClose(() => {
      console.warn('[rm] Socket closed');
      connected = false;

      const newSessionToken = getClientSessionToken();

      if (!!newSessionToken && newSessionToken !== sessionToken) {
        console.log('[rm] Session token changed, reconnecting');
        initializeSocket();
      }
    });

    // Clear the socket check interval
    if (socketCheckInterval) {
      clearInterval(socketCheckInterval);
      socketCheckInterval = null;
    }

    // Start checking socket connection every 5 seconds
    socketCheckInterval = setInterval(() => {
      checkSocketConnection(socket);
    }, socketCheckTimeout);
  }

  if (!connected && socket && !socket.isConnected()) {
    console.log('[rm] Connecting socket');
    socket.connect();
  }
};

export const disconnectSocket = () => {
  if (socket) {
    console.log('[rm] Disconnecting socket');
    socket.disconnect();
    socket = null;
    connected = false;

    // Clear the socket check interval
    if (socketCheckInterval) {
      clearInterval(socketCheckInterval);
      socketCheckInterval = null;
    }
  }
};

export const getSocket = () => {
  if (!socket) {
    throw new Error('Socket not initialized. Call initializeSocket() first.');
  }

  return socket;
};

export const joinChannel = (topic, params, handlers) => {
  if (channels[topic]) return channels[topic];

  const channel = getSocket().channel(topic, params);

  channel
    .join()
    .receive('ok', () => {
      handlers.onJoin?.();
    })
    .receive('error', err => {
      handlers.onError?.(err);
    });

  if (handlers.onMessage) {
    channel.on('message', handlers.onMessage);
  }

  channels[topic] = channel;

  return channel;
};

export const leaveChannel = topic => {
  if (channels[topic]) {
    channels[topic].leave();
    delete channels[topic];
  }
};

function checkSocketConnection(__socket) {
  if (!__socket || !__socket.isConnected()) {
    console.log('[rm] Socket connection lost, reconnecting');

    /* Socket can hang in 'disconnected', 'connecting', or 'disconnecting' states.
     * In such case isConnected() correctly returns false, but you cannot reconnect
     * the connection before dropping it manually, which we can do with disconnect(). */
    if (__socket) {
      __socket.disconnect();
    }

    socket = null;
    connected = false;

    initializeSocket();
  }
}
